import { CROPS } from './crops';
import { DECORATIONS } from './decorations';
import { itemIconKey, TX } from '../registry/keys';

export type ItemCategory = 'seed' | 'crop' | 'decoration' | 'material' | 'animal';

export interface ItemDef {
  id: string;
  name: string;
  category: ItemCategory;
  stackable: boolean;
  maxStack: number;
  /** Sell value in the shipping bin (0 = not sellable). */
  sellPrice: number;
  /** Shop buy price (undefined = not buyable). */
  price?: number;
  /** Short flavour for popups/shop. */
  description: string;
  /** Asset key for the inventory icon. */
  icon: string;
  /** Seeds only: crop id this seed plants. */
  plantsCrop?: string;
  /** If >0, the item is edible — tap it in the bag to eat and restore this much energy. */
  energy?: number;
}

/** Seed + produce items are derived directly from CROPS — no duplication. */
function cropItems(): Record<string, ItemDef> {
  const out: Record<string, ItemDef> = {};
  for (const crop of Object.values(CROPS)) {
    out[crop.seedItemId] = {
      id: crop.seedItemId,
      name: `${crop.name} Seeds`,
      category: 'seed',
      stackable: true,
      maxStack: 99,
      sellPrice: 0,
      price: crop.seedPrice,
      description: `Plant in tilled soil to grow ${crop.name}.`,
      icon: itemIconKey(crop.seedItemId),
      plantsCrop: crop.id,
    };
    out[crop.produceItemId] = {
      id: crop.produceItemId,
      name: crop.name,
      category: 'crop',
      stackable: true,
      maxStack: 99,
      sellPrice: crop.sellPrice,
      description: crop.description,
      icon: itemIconKey(crop.produceItemId),
    };
  }
  return out;
}

function decorationItems(): Record<string, ItemDef> {
  const out: Record<string, ItemDef> = {};
  for (const d of Object.values(DECORATIONS)) {
    out[d.id] = {
      id: d.id,
      name: d.name,
      category: 'decoration',
      stackable: true,
      maxStack: 99,
      sellPrice: 0,
      price: d.price,
      description: d.description,
      icon: d.icon,
    };
  }
  return out;
}

const SPECIAL: Record<string, ItemDef> = {
  wood: {
    id: 'wood', name: 'Wood', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 5, description: 'Chopped from a tree. Sells for a little.', icon: itemIconKey('wood'),
  },
  egg: {
    id: 'egg', name: 'Egg', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 25, energy: 12, description: 'A fresh egg from your chicken.', icon: TX.egg,
  },
  apple: {
    id: 'apple', name: 'Apple', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 30, energy: 15, description: 'Picked fresh from your apple tree.', icon: itemIconKey('apple'),
  },
  fish: {
    id: 'fish', name: 'Fish', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 45, energy: 10, description: 'Caught fresh from the pond.', icon: itemIconKey('fish'),
  },
  fish_bass: {
    id: 'fish_bass', name: 'Bass', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 90, description: 'A fine catch.', icon: itemIconKey('fish_bass'),
  },
  fish_carp: {
    id: 'fish_carp', name: 'Carp', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 35, description: 'A common pond fish.', icon: itemIconKey('fish_carp'),
  },
  fish_perch: {
    id: 'fish_perch', name: 'Perch', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 60, description: 'Striped and tasty.', icon: itemIconKey('fish_perch'),
  },
  fish_trout: {
    id: 'fish_trout', name: 'Rainbow Trout', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 150, description: 'A rare beauty from the pond!', icon: itemIconKey('fish_trout'),
  },
  // ---- cooked dishes (made at the stove; edible for energy or sellable) ----
  omelette: {
    id: 'omelette', name: 'Omelette', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 90, energy: 35, description: 'Fluffy and filling.', icon: itemIconKey('omelette'),
  },
  garden_salad: {
    id: 'garden_salad', name: 'Garden Salad', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 80, energy: 30, description: 'Crisp and fresh.', icon: itemIconKey('garden_salad'),
  },
  fruit_bowl: {
    id: 'fruit_bowl', name: 'Fruit Bowl', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 85, energy: 30, description: 'Sweet and juicy.', icon: itemIconKey('fruit_bowl'),
  },
  veggie_stew: {
    id: 'veggie_stew', name: 'Veggie Stew', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 140, energy: 50, description: 'Hearty and warm.', icon: itemIconKey('veggie_stew'),
  },
  pumpkin_pie: {
    id: 'pumpkin_pie', name: 'Pumpkin Pie', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 220, energy: 60, description: 'A whole-farm favorite.', icon: itemIconKey('pumpkin_pie'),
  },
  fish_dinner: {
    id: 'fish_dinner', name: 'Fish Dinner', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 120, energy: 40, description: 'Catch of the day.', icon: itemIconKey('fish_dinner'),
  },
  // ---- forageables (from the forest) ----
  berry: {
    id: 'berry', name: 'Wild Berries', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 20, energy: 12, description: 'Foraged from the forest.', icon: itemIconKey('berry'),
  },
  mushroom: {
    id: 'mushroom', name: 'Mushroom', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 25, energy: 10, description: 'A tasty forest find.', icon: itemIconKey('mushroom'),
  },
  herb: {
    id: 'herb', name: 'Herb', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 30, description: 'A fragrant wild herb.', icon: itemIconKey('herb'),
  },
  // ---- beach forageables ----
  shell: {
    id: 'shell', name: 'Seashell', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 30, description: 'A pretty shell from the beach.', icon: itemIconKey('shell'),
  },
  starfish: {
    id: 'starfish', name: 'Starfish', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 45, description: 'A five-armed beauty.', icon: itemIconKey('starfish'),
  },
  seaweed: {
    id: 'seaweed', name: 'Seaweed', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 18, energy: 8, description: 'Salty and nutritious.', icon: itemIconKey('seaweed'),
  },
  // ---- ocean fish (beach fishing) ----
  crab: {
    id: 'crab', name: 'Crab', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 60, description: 'A snappy ocean catch.', icon: itemIconKey('crab'),
  },
  squid: {
    id: 'squid', name: 'Squid', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 90, description: 'Inky and elusive.', icon: itemIconKey('squid'),
  },
  clam: {
    id: 'clam', name: 'Clam', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 45, description: 'Found in the shallows.', icon: itemIconKey('clam'),
  },
  // ---- bugs ----
  butterfly: {
    id: 'butterfly', name: 'Butterfly', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 40, description: 'A fluttering beauty.', icon: itemIconKey('butterfly'),
  },
  beetle: {
    id: 'beetle', name: 'Beetle', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 35, description: 'A shiny little bug.', icon: itemIconKey('beetle'),
  },
  // ---- mine minerals ----
  stone: {
    id: 'stone', name: 'Stone', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 8, description: 'Plain rock from the mine.', icon: itemIconKey('stone'),
  },
  coal: {
    id: 'coal', name: 'Coal', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 25, description: 'Burns hot.', icon: itemIconKey('coal'),
  },
  copper_ore: {
    id: 'copper_ore', name: 'Copper Ore', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 45, description: 'A shiny ore.', icon: itemIconKey('copper_ore'),
  },
  iron_ore: {
    id: 'iron_ore', name: 'Iron Ore', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 70, description: 'A sturdy ore.', icon: itemIconKey('iron_ore'),
  },
  gem: {
    id: 'gem', name: 'Gem', category: 'material', stackable: true, maxStack: 99,
    sellPrice: 180, description: 'A sparkling treasure!', icon: itemIconKey('gem'),
  },
  jewelry: {
    id: 'jewelry', name: 'Jewelry', category: 'material', stackable: true, maxStack: 99,
    sellPrice: 340, description: 'A cut gem set in gold — sells very well.', icon: itemIconKey('jewelry'),
  },
  cake: {
    id: 'cake', name: 'Birthday Cake', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 100, energy: 50, description: 'A special birthday treat! 🎂', icon: itemIconKey('cake'),
  },
  copper_bar: {
    id: 'copper_bar', name: 'Copper Bar', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 90, description: 'Smelted from ore + coal.', icon: itemIconKey('copper_bar'),
  },
  iron_bar: {
    id: 'iron_bar', name: 'Iron Bar', category: 'material', stackable: true, maxStack: 999,
    sellPrice: 150, description: 'Smelted from ore + coal.', icon: itemIconKey('iron_bar'),
  },
  honey: {
    id: 'honey', name: 'Honey', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 100, energy: 25, description: 'Sweet golden honey.', icon: itemIconKey('honey'),
  },
  fertilizer: {
    id: 'fertilizer', name: 'Fertilizer', category: 'material', stackable: true, maxStack: 99,
    sellPrice: 0, price: 40, description: 'Tap it in your bag to grow every crop a little.', icon: itemIconKey('fertilizer'),
  },
  chicken: {
    id: 'chicken', name: 'Chicken', category: 'animal', stackable: false, maxStack: 1,
    sellPrice: 0, price: 300, description: 'Lays an egg each morning. Buy one for the farm!', icon: TX.chicken,
  },
  cow: {
    id: 'cow', name: 'Cow', category: 'animal', stackable: false, maxStack: 1,
    sellPrice: 0, price: 800, description: 'Tap to milk her each morning — hearty profit.', icon: TX.cow,
  },
  milk: {
    id: 'milk', name: 'Milk', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 80, energy: 20, description: 'Fresh and creamy.', icon: itemIconKey('milk'),
  },
  sheep: {
    id: 'sheep', name: 'Sheep', category: 'animal', stackable: false, maxStack: 1,
    sellPrice: 0, price: 500, description: 'Tap to shear her soft wool each morning.', icon: TX.sheep,
  },
  wool: {
    id: 'wool', name: 'Wool', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 70, description: 'Soft, warm, and fluffy.', icon: itemIconKey('wool'),
  },
  pig: {
    id: 'pig', name: 'Pig', category: 'animal', stackable: false, maxStack: 1,
    sellPrice: 0, price: 650, description: 'Tap to gather a truffle each morning — worth a fortune.', icon: TX.pig,
  },
  truffle: {
    id: 'truffle', name: 'Truffle', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 160, description: 'A rare delicacy your pig snuffled up.', icon: itemIconKey('truffle'),
  },
  duck: {
    id: 'duck', name: 'Duck', category: 'animal', stackable: false, maxStack: 1,
    sellPrice: 0, price: 350, description: 'Tap to collect a duck egg each morning.', icon: TX.duck,
  },
  duck_egg: {
    id: 'duck_egg', name: 'Duck Egg', category: 'crop', stackable: true, maxStack: 99,
    sellPrice: 45, energy: 16, description: 'A big, rich duck egg.', icon: itemIconKey('duck_egg'),
  },
};

export const ITEMS: Record<string, ItemDef> = { ...cropItems(), ...decorationItems(), ...SPECIAL };

export const getItem = (id: string): ItemDef | undefined => ITEMS[id];
